import {
  Dialog,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material'
import React from 'react'
import GooglePlacesAutocomplete from 'react-google-autocomplete'

export interface AddressDTO {
  address: string
  zipCode: string
  city: string
  country: string
}

type Props = {
  onChange: (address: AddressDTO) => void
  zipCode: string
  city: string
  apiKey: string
  isOpen: boolean
  onClose: () => void
}

export const AddressAutocomplete = ({
  onChange,
  apiKey,
  isOpen,
  onClose,
}: Props) => {
  console.log('API KEY', apiKey)
  const [selectedAddress, setSelectedAddress] =
    React.useState<AddressDTO | null>(null)
  const [key, setKey] = React.useState(0)
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    setKey((prev) => prev + 1)
    setSelectedAddress(null)
    if (isOpen) {
      setTimeout(() => {
        document.getElementById('autocomplete-input')?.focus()
      }, 200)
    }
  }, [isOpen])

  const handlePlaceSelect = (place: any) => {
    if (!place.geometry || !place.geometry.location) {
      window.alert('No details available for input: \'' + place.name + '\'')
      return
    }

    const newAddress: AddressDTO = {
      address: '',
      zipCode: '',
      city: '',
      country: '',
    }

    if (place.address_components) {
      for (const component of place.address_components) {
        if (component.types.includes('street_number')) {
          newAddress.address = component.long_name
        }
        if (component.types.includes('route')) {
          newAddress.address = newAddress.address
            ? `${newAddress.address} ${component.long_name}`
            : component.long_name
        }
        if (
          component.types.includes('sublocality_level_1') ||
          component.types.includes('neighborhood')
        ) {
          newAddress.address = newAddress.address
            ? `${newAddress.address}, ${component.long_name}`
            : component.long_name
        }
        for (let i = 0; i < component.types.length; i++) {
          if (component.types[i] === 'postal_code') {
            newAddress.zipCode = component.long_name
          }
          if (component.types[i] === 'postal_code_suffix') {
            newAddress.zipCode = newAddress.zipCode
              ? `${newAddress.zipCode}-${component.long_name}`
              : component.long_name
          }
        }
        if (
          (component.types.includes('locality') ||
            component.types.includes('administrative_area_level_2')) &&
          !newAddress.city
        ) {
          newAddress.city = component.long_name
        }
        if (component.types.includes('country')) {
          newAddress.country = component.long_name
        }
      }

      if (place.name && !newAddress.address.includes(place.name)) {
        newAddress.address =
          place.name + (newAddress.address ? `, ${newAddress.address}` : '')
      }
    }

    if (!newAddress.address && place.formatted_address) {
      newAddress.address = place.formatted_address
    }

    if (!newAddress.zipCode && place.formatted_address) {
      const zipMatch = place.formatted_address.match(/\b\d{5}(?:-\d{4})?\b/)
      if (zipMatch) newAddress.zipCode = zipMatch[0]
    }

    setSelectedAddress(newAddress)
  }

  const handleConfirm = () => {
    if (selectedAddress) {
      onChange(selectedAddress)
      onClose()
    }
  }

  return isOpen ? (
    <Dialog
      sx={{
        '& .MuiDialog-paper': {
          top: '100px',
          minWidth: '400px',
          maxWidth: '600px',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
        },
      }}
      TransitionProps={{
        timeout: 150,
      }}
      open={isOpen}
      onClose={onClose}
      disablePortal
    >
      <div className="w-full relative">
        <style>{`
          .pac-container {
            z-index: 9999;
            position: fixed !important;
            transform: translateY(40px);
            border-radius: 8px;
            box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
          }
        `}</style>
        <GooglePlacesAutocomplete
          ref={inputRef}
          key={`autocomplete-${key}`}
          id="autocomplete-input"
          apiKey={apiKey}
          onPlaceSelected={handlePlaceSelect}
          options={{
            // country: 'fr',
            fields: [
              'address_components',
              'formatted_address',
              'geometry',
              'name',
              'types',
            ],
            types: ['geocode', 'establishment'],
          }}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          defaultValue=""
          placeholder="Entrez un lieu (ville, quartier, rue ou établissement)"
        />

        {selectedAddress && (
          <Table size="small" sx={{ mt: 2, mb: 2 }}>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>
                  Adresse
                </TableCell>
                <TableCell>{selectedAddress.address}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Code postal</TableCell>
                <TableCell>{selectedAddress.zipCode}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Ville</TableCell>
                <TableCell>{selectedAddress.city}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Pays</TableCell>
                <TableCell>{selectedAddress.country}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outlined" onClick={onClose}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!selectedAddress}
          >
            Confirmer
          </Button>
        </div>
      </div>
    </Dialog>
  ) : null
}

export default AddressAutocomplete
